import { useEffect, useRef, type ReactNode } from "react";
// The explicit React import keeps this module renderable by the node:test
// scenario suite, which compiles JSX to the classic runtime.
import React from "react";
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  LinkIcon,
  List,
  ListOrdered,
  Redo2,
  Undo2,
} from "lucide-react";
import { EditorState } from "@tiptap/pm/state";
import { cn } from "../../lib/utils";
import {
  richTextEditorExtensions,
  type RichTextDocument,
} from "../../../../shared/rich-text-document";

/** Note typography, shared with the read-only Note view so both render alike. */
export const NOTE_PROSE_CLASSES =
  "bg-card px-5 py-4 text-body-lg leading-[21px] text-muted-strong [&_.ProseMirror]:outline-none [&_.ProseMirror_h1]:mb-2 [&_.ProseMirror_h1]:mt-4 [&_.ProseMirror_h1:first-child]:mt-0 [&_.ProseMirror_h1]:font-sans [&_.ProseMirror_h1]:text-lg [&_.ProseMirror_h1]:font-semibold [&_.ProseMirror_h1]:text-foreground [&_.ProseMirror_h2]:mb-1 [&_.ProseMirror_h2]:mt-3 [&_.ProseMirror_h2:first-child]:mt-0 [&_.ProseMirror_h2]:font-sans [&_.ProseMirror_h2]:text-base [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:text-foreground [&_.ProseMirror_h3]:mb-1 [&_.ProseMirror_h3]:mt-3 [&_.ProseMirror_h3:first-child]:mt-0 [&_.ProseMirror_h3]:font-sans [&_.ProseMirror_h3]:text-sm [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:text-foreground [&_.ProseMirror_ol]:ml-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_p]:my-0 [&_.ProseMirror_ul]:ml-5 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline";

export interface RichTextEditorProps {
  initialValue: RichTextDocument;
  replacement?: RichTextDocument;
  disabled?: boolean;
  toolbarEnd?: ReactNode;
  alert?: ReactNode;
  /** Pinned under the text inside the card: actions and status that must stay reachable. */
  footer?: ReactNode;
  shouldFillAvailableHeight?: boolean;
  onChange: (value: RichTextDocument, plainText: string) => void;
}

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center justify-center rounded-md border border-transparent px-2 text-muted-foreground transition-colors hover:border-border hover:bg-card hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50",
        active && "border-primary bg-primary-soft text-primary"
      )}
    >
      {children}
    </button>
  );
}

/** Applies a replacement document silently, so no undo can reach back past it. */
export function replaceContentAndResetHistory(
  editor: Editor,
  replacement: RichTextDocument
) {
  // Rebuilding state drops the undo stack so Undo cannot resurrect text the RD gave up.
  editor.view.updateState(
    EditorState.create({ doc: editor.state.doc, plugins: editor.state.plugins })
  );
  // This real transaction also refreshes toolbar subscribers; history ignores it.
  editor
    .chain()
    .setMeta("addToHistory", false)
    .setContent(replacement, { emitUpdate: false })
    .run();
}

function setLink(editor: Editor) {
  const previousUrl = editor.getAttributes("link").href as string | undefined;
  const url = window.prompt("Paste link", previousUrl ?? "");
  if (url === null) return;

  if (!url.trim()) {
    editor.chain().focus().unsetLink().run();

    return;
  }

  editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
}

export function toolbarState(currentEditor: Editor | null) {
  const isActive = (name: string, attributes?: Record<string, unknown>) =>
    currentEditor?.isActive(name, attributes) ?? false;

  return {
    canUndo: currentEditor?.can().undo() ?? false,
    canRedo: currentEditor?.can().redo() ?? false,
    heading1: isActive("heading", { level: 1 }),
    heading2: isActive("heading", { level: 2 }),
    heading3: isActive("heading", { level: 3 }),
    bold: isActive("bold"),
    italic: isActive("italic"),
    bulletList: isActive("bulletList"),
    orderedList: isActive("orderedList"),
    link: isActive("link"),
  };
}

type ToolbarState = ReturnType<typeof toolbarState>;

function EditorToolbar(props: {
  editor: Editor;
  disabled?: boolean;
  toolbarEnd?: ReactNode;
}) {
  const state = useEditorState({
    editor: props.editor,
    selector: ({ editor: currentEditor }) => toolbarState(currentEditor),
  });

  return <ToolbarContent {...props} state={state} />;
}

function HistoryButtons({
  editor,
  state,
  disabled,
}: {
  editor: Editor;
  state: ToolbarState | null;
  disabled?: boolean;
}) {
  return (
    <>
      <ToolbarButton
        label="Undo"
        disabled={disabled || !state?.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Redo"
        disabled={disabled || !state?.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="h-4 w-4" />
      </ToolbarButton>
    </>
  );
}

function HeadingButtons({
  editor,
  state,
  disabled,
}: {
  editor: Editor;
  state: ToolbarState | null;
  disabled?: boolean;
}) {
  return (
    <>
      <ToolbarButton
        label="Heading 1"
        active={state?.heading1}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Heading 2"
        active={state?.heading2}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Heading 3"
        active={state?.heading3}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </ToolbarButton>
    </>
  );
}

function MarkButtons({
  editor,
  state,
  disabled,
}: {
  editor: Editor;
  state: ToolbarState | null;
  disabled?: boolean;
}) {
  return (
    <>
      <ToolbarButton
        label="Bold"
        active={state?.bold}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={state?.italic}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
    </>
  );
}

function ListButtons({
  editor,
  state,
  disabled,
}: {
  editor: Editor;
  state: ToolbarState | null;
  disabled?: boolean;
}) {
  return (
    <>
      <ToolbarButton
        label="Bullet list"
        active={state?.bulletList}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={state?.orderedList}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
    </>
  );
}

export function ToolbarContent({
  editor,
  state,
  disabled,
  toolbarEnd,
}: {
  editor: Editor;
  state: ToolbarState | null;
  disabled?: boolean;
  toolbarEnd?: ReactNode;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-border bg-background px-2 py-1">
      <HistoryButtons editor={editor} state={state} disabled={disabled} />
      <span className="mx-1 h-5 w-px bg-border" />
      <HeadingButtons editor={editor} state={state} disabled={disabled} />
      <span className="mx-1 h-5 w-px bg-border" />
      <MarkButtons editor={editor} state={state} disabled={disabled} />
      <span className="mx-1 h-5 w-px bg-border" />
      <ListButtons editor={editor} state={state} disabled={disabled} />
      <span className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton
        label="Link"
        active={state?.link}
        disabled={disabled}
        onClick={() => setLink(editor)}
      >
        <LinkIcon className="h-4 w-4" />
      </ToolbarButton>
      {toolbarEnd ? (
        <div className="ml-auto flex items-center pl-2">{toolbarEnd}</div>
      ) : null}
    </div>
  );
}

export function RichTextEditor({
  initialValue,
  replacement,
  disabled,
  toolbarEnd,
  alert,
  footer,
  shouldFillAvailableHeight,
  onChange,
}: RichTextEditorProps) {
  const initialValueRef = useRef(initialValue);
  const initialEditableRef = useRef(!disabled);
  const editor = useEditor({
    extensions: richTextEditorExtensions,
    content: initialValueRef.current,
    editable: initialEditableRef.current,
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor: updatedEditor }) => {
      const document = updatedEditor.getJSON() as RichTextDocument;
      const plainText = updatedEditor.getText().replace(/\s+/g, " ").trim();
      queueMicrotask(() => onChange(document, plainText));
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled, false);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor || !replacement) return;

    replaceContentAndResetHistory(editor, replacement);
  }, [editor, replacement]);

  if (!editor) {
    return (
      <div className="min-h-[180px] rounded-[10px] border border-border bg-background brand:rounded-xl" />
    );
  }

  return (
    <div
      className={cn(
        // Under the always theme the card takes the theme's 22px card radius (NO-799).
        "flex overflow-hidden rounded-[10px] border border-border bg-card brand:rounded-xl",
        shouldFillAvailableHeight ? "h-full min-h-0 flex-col" : "flex-col"
      )}
    >
      <EditorToolbar editor={editor} disabled={disabled} toolbarEnd={toolbarEnd} />
      {alert}
      <EditorContent
        editor={editor}
        className={cn(
          NOTE_PROSE_CLASSES,
          shouldFillAvailableHeight
            ? "min-h-0 flex-1 overflow-y-auto [&_.ProseMirror]:min-h-full"
            : "min-h-[180px] [&_.ProseMirror]:min-h-[156px]"
        )}
      />
      {footer}
    </div>
  );
}
